import { AppSettings } from "./BTCTypes";

export class ReportErrorToServer {
    public static readonly BAD_REQUEST: number = 400; // Sent by ASP.Net Core when property validation fails (eg [Required(ErrorMessage = "Please enter a Role Name")])
    public static readonly UNAUTHORIZED: number = 401; // User is unknown
    public static readonly FORBIDDEN: number = 403; // User is known, but not authorized
    public static readonly NOTFOUND: number = 404; // BTCAPI missing authorization cookie, misnamed controller or action method
    public static readonly UNPROCESSABLE_ENTITY: number = 422; // Unprocessable Entity
    public static readonly INTERNAL_SERVER_ERROR: number = 500;
    public static readonly SERVICE_UNAVAILABLE: number = 503;
    public static readonly SERVER_SIDE_RECAPTCHA_FAILED: number = 550;
    public static readonly BTCAPI_EXCEPTION: number = 552;

    // From: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Guide/Using_promises#creating_a_promise_around_an_old_callback_api
    private static sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

    public static async ReportClientErrorToServer(errorMessage: string, errorFrom: string, error: unknown | null = null) {
        const URL = AppSettings.BTCAPIAddress() + "/BTCAPI/ReportClientSideError/ClientError";
        const fd = new FormData();
        fd.append("errorFrom", errorFrom);
        fd.append("errorMessage", errorMessage ?? '');
        const e = (error instanceof Error ? error.message
            : (error instanceof TypeError ? `${error.message}; ; Stack Trace: ${error.stack}}`
                : (error instanceof String ? error as String : ""))
        );
        fd.append("error", e.toString());
        fd.append("errorStack", (error instanceof Error ? error.stack : "")?.toString() ?? "");
        await fetch(URL, { method: "POST", body: fd });

        await this.sleep(1500); // Short throttle (plus server's own throttle) incase this is happening inside a loop.
    }


    // Analyze ajax error and determine whether to show to end user and if need to
    // request unexpected error notification email from server
    public static async DisplayAndProcessAjaxFailure2(
        PostToAPIUrl: string,
        HttpVerb: string,
        statusCode: number,
        formData: FormData | null,
        DiagnosticMsg: string,
        Exception: Error | null = null
    ): Promise<void> {
        let EndUserErrMsg = "";

        // Stringify form data, if any
        let sFormInfo: string = "";
        if (formData !== null)
            for (var pair of formData.entries())
                sFormInfo += pair[0] + ": " + pair[1] + "; ";

        switch (statusCode) {
            case 0: // No response from server?
                EndUserErrMsg = "The BTC server is not responding. Either it's inaccessible/offline or your internet connection is down.";
                break;

            case ReportErrorToServer.SERVICE_UNAVAILABLE: // Web server is working, but ASP.Net Core web API isn't responding
                EndUserErrMsg = "The BTC web API is offline";
                break;

            case ReportErrorToServer.BAD_REQUEST: // Probably a model validation failed in ASP.Net Core or bad URL or missing anti-forgery token
                EndUserErrMsg += "<br>Bad request";
                break;

            case ReportErrorToServer.UNAUTHORIZED:
                const PageAddr = window.location.href.toString().split(window.location.host)[1];
                EndUserErrMsg = "Unauthorized. You need to <a href='/SignIn?ReturnUrl=" + PageAddr + "'>sign-in</a>";
                break;

            case ReportErrorToServer.FORBIDDEN:
                EndUserErrMsg = "You're logged in, but don't have the correct authorization to access this page";
                break;

            case ReportErrorToServer.NOTFOUND: // eg Missing API authorization cookie, misnamed controller or action method
                EndUserErrMsg = "Server returned 404 not found. The Powers That Be have been notified (with any luck)";
                break;

            case ReportErrorToServer.UNPROCESSABLE_ENTITY: // Business rule violation (eg Duplicate record exists)
                EndUserErrMsg = "Unprocessable entity";
                break;

            case ReportErrorToServer.SERVER_SIDE_RECAPTCHA_FAILED: // Have a basic ProblemDetails message (eg auth failure)
                EndUserErrMsg = "Recaptcha validation failed 😟; Google thinks you're a bot 🤖";
                break;

            case ReportErrorToServer.BTCAPI_EXCEPTION:
                // eg ProblemDetails with string error msg or possibly an exception in json for Dev only (eg sql syntax error)
                EndUserErrMsg += "Unexpected BTC error";
                break;

            default: // Unexpected error, so send email
                EndUserErrMsg = "Unexpected server error; The Powers That Be have been notified (possibly)";
                await this.sleep(1500); // Short throttle (plus server's own throttle) incase this is happening inside a loop.
                break;
        }

        let errmsg = EndUserErrMsg + "<br>" + DiagnosticMsg;
        if (Exception)
            errmsg += "<br>" + Exception.message;
        await this.ReportAjaxFailToServer2(statusCode, errmsg, PostToAPIUrl, HttpVerb, sFormInfo);
        (document.getElementById("ErrorMessage") as HTMLDivElement).innerHTML = EndUserErrMsg;
    }

    private static async ReportAjaxFailToServer2(statusCode: number, EndUserErrMsg: string, requestURL: string,
        HttpVerb: string, sFormInfo: string) {

        // Write error to console
        let TruncatedResponseText = "";

        if (console && console.log) {
            const ErrMsgForConsole: string =
                "End User Msg: " + EndUserErrMsg
                + "; jqXHR.responseText: " + TruncatedResponseText
                + "; FormParameters: " + sFormInfo
                ;
            console.log(ErrMsgForConsole);
        }

        // Send error details to server to send error email
        const URL = AppSettings.BTCAPIAddress() + "/BTCAPI/ReportClientSideError/AjaxFailure";
        const fd = new FormData();
        fd.append("CurrentWebPage", window.location.href);
        fd.append("EndUserErrMsg", EndUserErrMsg);
        fd.append("HttpVerb", HttpVerb);
        fd.append("RequestURL", requestURL);
        fd.append("FormParameters", sFormInfo);
        fd.append("StatusText", statusCode.toString());
        fd.append("TruncatedResponseText", TruncatedResponseText);

        await fetch(URL, { method: "POST", body: fd });
    }
}
